import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  BadgeCheck,
  CalendarClock,
  Check,
  Clock3,
  Copy,
  FileText,
  Handshake,
  KeyRound,
  LoaderCircle,
  Mail,
  RefreshCw,
  ShoppingCart,
  TriangleAlert,
} from 'lucide-react';

import type { CapturePointV6ContactInput, CapturePointV6LicenseView } from '../shared/desktop-api';
import {
  buyerFacingLicenseError,
  capturePointStationPricing,
  licenseBroadcastClearsMessages,
  licenseContactLimits,
  licenseContactProblems,
  licenseContactProblemSummary,
  licenseSupportRecoveryMessage,
  normalizePartnerCode,
  openCheckoutMessage,
  partnerLookupWithDeadline,
  trialOffers,
  type CapturePointCustomerDiscount,
  type CheckoutPlan,
} from '../shared/engagement';
import { takeLicenseFrom, takeLicenseIntent, useEngagement } from './EngagementPanels';
import { useEmailAsks } from './EmailAsk';
import { EXTRA_WEEK_TIPS_LINE } from '../shared/email-asks';
import './email-ask.css';
import { reportEvent } from './telemetry';
import { PageAllowancePanel, usePageAllowance } from './PageAllowance';
import { PlanPage, litePrice, formatPrice, type PlanKey } from './PlanPage';
import type { TelemetryUiErrorCode } from '../shared/telemetry-catalog';

type LicenseAction =
  'extend' | 'activate' | 'buy' | 'questions' | 'checkout' | 'more-time' | 'change-plan';

/** The live partner-code check: the partner's name shows, so a typo can't lose their credit. */
export type PartnerCheck =
  | { state: 'empty' }
  /** Being typed and not a code yet ("M", "MIL"): neutral, never an error, until the input settles or leaves the field. */
  | { state: 'typing' }
  | { state: 'malformed' }
  | { state: 'checking'; code: string }
  /** `discount`: the partner's customer discount, when Phone Home answered one (the plan page's prices). */
  | { state: 'accepted'; code: string; discount?: CapturePointCustomerDiscount }
  | { state: 'not-recognized'; code: string }
  | { state: 'unavailable'; code: string };

/**
 * The code to send with a purchase, or undefined; `blocked` only while the
 * code can't be a code yet (half typed) or is still being checked. A code the
 * check didn't recognize is still sent: a lockout looks exactly like a miss,
 * so a real code must never stop a purchase. Phone Home attaches it only if it
 * is a partner's, and otherwise keeps it for Ademero to follow up.
 */
export const partnerCodeForPurchase = (check: PartnerCheck): { blocked: boolean; code?: string } =>
  check.state === 'empty'
    ? { blocked: false }
    : check.state === 'accepted' ||
        check.state === 'unavailable' ||
        check.state === 'not-recognized'
      ? { blocked: false, code: check.code }
      : { blocked: true };

/**
 * One answer per code for the session: the same code is never checked twice (each check counts against the
 * lockout and the daily limit). A check still in flight is shared too, so retyping the same code (a character
 * added and removed, a space or dash) while it runs never sends a second one; only a settled "couldn't check"
 * is asked again.
 */
export const partnerLookupCache = () => {
  const answers = new Map<string, PartnerCheck>();
  const pending = new Map<string, Promise<PartnerCheck>>();
  // Only real answers are kept; "couldn't check" is asked again next time.
  const remember = (check: PartnerCheck) => {
    if (check.state === 'accepted' || check.state === 'not-recognized')
      answers.set(check.code, check);
  };
  return {
    get: (code: string) => answers.get(code),
    remember,
    check: (code: string, run: () => Promise<PartnerCheck>): Promise<PartnerCheck> => {
      const known = answers.get(code);
      if (known) return Promise.resolve(known);
      const inFlight = pending.get(code);
      if (inFlight) return inFlight;
      const next = run()
        .then((check) => {
          remember(check);
          return check;
        })
        .finally(() => pending.delete(code));
      pending.set(code, next);
      return next;
    },
  };
};

/** What the buy screen says about an invoice request waiting with Ademero (or one Ademero turned down, or one a card payment settled). */
export const pendingInvoiceMessage = (
  request: {
    plan: CheckoutPlan;
    submittedUtc: string;
    state?: 'pending' | 'denied' | 'paid-by-card';
  } | null,
): string | null =>
  request === null
    ? null
    : request.state === 'denied'
      ? "Ademero couldn't approve the invoice request. Buy by card, or contact Ademero."
      : request.state === 'paid-by-card'
        ? "Card payment received, so the invoice request was withdrawn. This computer activates shortly; if it doesn't within the hour, contact Ademero."
        : `Invoice requested ${new Date(request.submittedUtc).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })} (${request.plan === 'yearly' ? `$${capturePointStationPricing.yearlyUsd.toLocaleString('en-US')} a year` : `$${capturePointStationPricing.monthlyUsd} a month`}). Ademero emails the invoice, and this computer activates when it's approved.`;

const planPrice = (plan: CheckoutPlan): string =>
  plan === 'yearly'
    ? `$${capturePointStationPricing.yearlyUsd.toLocaleString('en-US')} a year`
    : `$${capturePointStationPricing.monthlyUsd} a month`;

type ContactDraft = {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
};

const emptyContact: ContactDraft = {
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
};

const remainingLabel = (seconds: number | null): string => {
  if (seconds === null) return 'No end date available';
  if (seconds <= 0) return 'Expired';
  if (seconds >= 48 * 60 * 60) return `${Math.ceil(seconds / (24 * 60 * 60))} days left`;
  if (seconds >= 2 * 60 * 60) return `${Math.ceil(seconds / (60 * 60))} hours left`;
  return `${Math.max(1, Math.ceil(seconds / 60))} minutes left`;
};

const dateLabel = (value: string | null): string =>
  value === null
    ? 'Not available'
    : new Date(value).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/** The staff-reviewed more-time request, in one line. The days apply only through the signed license. */
export const moreTimeRequestMessage = (state: CapturePointV6LicenseView | null): string | null => {
  const request = state?.moreTimeRequest;
  if (
    !state ||
    !request ||
    (state.runtimeState !== 'trial-active' && state.runtimeState !== 'trial-expired')
  )
    return null;
  if (request.state === 'pending')
    return `Request sent ${dateLabel(request.submittedUtc)}. Ademero reviews it and will follow up.`;
  if (request.state === 'denied') return "Ademero didn't extend the trial. Buy now, or ask again.";
  const applied =
    request.trialExpiresUtc !== null &&
    state.expiresUtc !== null &&
    Date.parse(state.expiresUtc) >= Date.parse(request.trialExpiresUtc);
  return applied
    ? `Ademero added ${request.grantedDays} days. Your trial now ends ${dateLabel(state.expiresUtc)}.`
    : `Ademero added ${request.grantedDays} days. Checking the license to apply them...`;
};

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
const statusPresentation = (state: CapturePointV6LicenseView | null) => {
  switch (state?.runtimeState) {
    case 'trial-active':
      return { title: 'Free trial', detail: 'Everything is ready', tone: 'trial', icon: Clock3 };
    case 'active':
      return {
        title: 'Activated',
        detail: 'This computer is licensed',
        tone: 'active',
        icon: BadgeCheck,
      };
    case 'trial-expired':
      return {
        title: 'Trial ended',
        detail: state.canAddTrialDays
          ? 'Continue with a license or 7 more free days'
          : 'Your free trial is complete',
        tone: 'attention',
        icon: CalendarClock,
      };
    case 'license-expired':
      return {
        title: 'License expired',
        detail: 'Connect to renew this computer',
        tone: 'attention',
        icon: TriangleAlert,
      };
    case 'suspended':
      return {
        title: 'License paused',
        detail: 'Contact Ademero to continue',
        tone: 'attention',
        icon: TriangleAlert,
      };
    case 'deactivated':
      return {
        title: 'License removed',
        detail: 'A new activation is required',
        tone: 'attention',
        icon: TriangleAlert,
      };
    default:
      return {
        title: 'Checking license',
        detail: "Reading this computer's license",
        tone: 'checking',
        icon: Clock3,
      };
  }
};

// One rule for the buttons, the field markers and main (Phone Home's email check and lengths), so a click
// never does nothing without saying which detail to fix.
const contactReady = (draft: ContactDraft): boolean =>
  Object.keys(licenseContactProblems(draft)).length === 0;

/** The card checkout opened in the browser, kept for this window's session while main waits for the payment. */
const openCheckoutKey = 'capturepoint.license.open-checkout';
const readOpenCheckout = (): { plan: CheckoutPlan; openedUtc: string } | null => {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(openCheckoutKey) ?? 'null') as {
      plan?: unknown;
      openedUtc?: unknown;
    } | null;
    return value &&
      (value.plan === 'monthly' || value.plan === 'yearly') &&
      typeof value.openedUtc === 'string'
      ? { plan: value.plan, openedUtc: value.openedUtc }
      : null;
  } catch {
    return null;
  }
};
const writeOpenCheckout = (value: { plan: CheckoutPlan; openedUtc: string } | null) => {
  try {
    if (value) window.sessionStorage.setItem(openCheckoutKey, JSON.stringify(value));
    else window.sessionStorage.removeItem(openCheckoutKey);
  } catch {
    /* a convenience only */
  }
};

const toContact = (draft: ContactDraft): CapturePointV6ContactInput => ({
  companyName: draft.companyName.trim(),
  contactName: draft.contactName.trim(),
  email: draft.email.trim(),
  ...(draft.phone.trim() ? { phone: draft.phone.trim() } : {}),
});

/** Phone Home said only support can recover this computer: the message, the installation ID and a copy button. */
function SupportRecoveryNotice({ installationId }: { installationId: string }) {
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(installationId);
      setCopied('copied');
    } catch {
      setCopied('failed');
    }
  };
  return (
    <div
      className="license-message problem license-support-recovery"
      role="alert"
      data-telemetry="error:license-support-needed"
    >
      <TriangleAlert size={16} aria-hidden="true" />
      <span>{licenseSupportRecoveryMessage(installationId)}</span>
      <button
        type="button"
        className="secondary-button"
        onClick={() => void copy()}
        aria-label="Copy installation ID"
      >
        <Copy size={15} aria-hidden="true" />{' '}
        {copied === 'copied'
          ? 'Copied'
          : copied === 'failed'
            ? 'Select and copy the ID'
            : 'Copy ID'}
      </button>
    </div>
  );
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function LicenseSettings() {
  const [state, setState] = useState<CapturePointV6LicenseView | null>(null);
  const [draft, setDraft] = useState<ContactDraft>(emptyContact);
  const [action, setAction] = useState<LicenseAction | null>(null);
  const [busy, setBusy] = useState<'refresh' | LicenseAction | null>(null);
  const [plan, setPlan] = useState<CheckoutPlan>('yearly');
  const requestedIntent = useRef(takeLicenseIntent());
  // Where the person came here from (the tray, the trial-ended banner, a link), else here itself: sent with Buy now.
  const openedFrom = useRef(takeLicenseFrom() ?? 'license');
  const engagement = useEngagement();
  // D · Extra week (shared/email-asks.ts): while the email asks are on and this computer isn't getting
  // tips, Add 7 free days says in one line that tips come with the extra week, and turns them on with it.
  const emailAsks = useEmailAsks();
  const extraWeekTips =
    Boolean(emailAsks?.available) && engagement !== null && engagement.consent.state !== 'opted-in';
  // The first email given anywhere on this computer (an email ask, the tips sign-up) fills an empty Email here too.
  const rememberedEmail = emailAsks?.available ? emailAsks.prefill.email : '';
  useEffect(() => {
    if (rememberedEmail && !draftEdited.current)
      setDraft((current) => (current.email ? current : { ...current, email: rememberedEmail }));
  }, [rememberedEmail]);
  const [checkoutClosed, setCheckoutClosed] = useState(false);
  // Buttons stay clickable; a click with details missing says which ones (owner, 2026-09-28:
  // a greyed-out "Bill us instead" with a no-entry cursor gave no reason).
  const [showMissing, setShowMissing] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblemText] = useState<string | null>(null);
  const [problemCode, setProblemCode] = useState<TelemetryUiErrorCode | null>(null);
  /** A problem to show, and the code telemetry sends for it instead of its words (none: counted as a plain alert). */
  const setProblem = useCallback(
    (text: string | null, code: TelemetryUiErrorCode | null = null) => {
      setProblemText(text);
      setProblemCode(text ? code : null);
    },
    [],
  );
  const [note, setNote] = useState('');
  const [partnerInput, setPartnerInput] = useState('');
  const [partnerCheck, setPartnerCheck] = useState<PartnerCheck>({ state: 'empty' });
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [pendingInvoice, setPendingInvoice] = useState<{
    plan: CheckoutPlan;
    submittedUtc: string;
    state?: 'pending' | 'denied' | 'paid-by-card';
  } | null>(null);
  // A request Ademero turned down no longer waits: Buy now is offered again.
  const invoiceWaiting =
    pendingInvoice && pendingInvoice.state !== 'denied' && pendingInvoice.state !== 'paid-by-card'
      ? pendingInvoice
      : null;
  // Paid by card: the license follows the payment, so Buy now is never offered again meanwhile.
  const paidByCard = pendingInvoice?.state === 'paid-by-card';
  const [openCheckout, setOpenCheckout] = useState(readOpenCheckout);
  const [clockMs, setClockMs] = useState(() => Date.now());
  const lastState = useRef<CapturePointV6LicenseView | null>(null);
  useEffect(() => {
    lastState.current = state;
  }, [state]);
  const partnerAnswers = useRef(partnerLookupCache());
  const draftEdited = useRef(false);
  const opened = useRef(false);
  const live = useRef(true);
  // Guards only the first getV6LicenseState read: a broadcast or an action's reply that lands first is newer.
  // Actions never check it: main broadcasts "refreshing" and the result before every reply, so a reply
  // checked against it was always dropped (no confirmation, no reason for a failure).
  const firstReadRevision = useRef(0);
  const pendingAction = useRef<object | null>(null);
  // A purchase or invoice outcome is shown beside the button that caused it,
  // scrolled into view and focused so it is announced. On 6.2.0 a failed Buy now
  // printed its reason below the fold and the click looked like it did nothing.
  const messageRef = useRef<HTMLDivElement>(null);
  const checkoutLineRef = useRef<HTMLParagraphElement>(null);
  const closedRef = useRef<HTMLParagraphElement>(null);
  const [announce, setAnnounce] = useState<{
    seq: number;
    target: 'message' | 'checkout' | 'closed';
  } | null>(null);
  const announceOutcome = (target: 'message' | 'checkout' | 'closed') =>
    setAnnounce((current) => ({ seq: (current?.seq ?? 0) + 1, target }));
  useEffect(() => {
    if (!announce) return;
    const element =
      announce.target === 'checkout'
        ? checkoutLineRef.current
        : announce.target === 'closed'
          ? closedRef.current
          : messageRef.current;
    if (!element) return;
    element.scrollIntoView({ block: 'nearest' });
    element.focus({ preventScroll: true });
  }, [announce]);

  useEffect(() => {
    live.current = true;
    const api = window.capturePointDesktop;
    if (!api) {
      setProblem('License controls are unavailable in this build.', 'license-load-failed');
      return () => {
        live.current = false;
      };
    }
    const stop = api.onV6LicenseStateChanged((next) => {
      if (!live.current) return;
      firstReadRevision.current += 1;
      // Main publishes on every refresh (every 20 s while a checkout is open): only a change in the license
      // clears the last confirmation or problem, which the buyer may still need to read.
      const clears = licenseBroadcastClearsMessages(lastState.current, next);
      lastState.current = next;
      setState(next);
      if (clears) {
        setProblem(null);
        setNotice(null);
      }
    });
    const request = ++firstReadRevision.current;
    void api
      .getV6LicenseState()
      .then((next) => {
        if (live.current && firstReadRevision.current === request) {
          lastState.current = next;
          setState(next);
        }
      })
      .catch(() => {
        if (live.current && firstReadRevision.current === request)
          setProblem('Paige could not read the license on this computer.', 'license-load-failed');
      });
    return () => {
      live.current = false;
      firstReadRevision.current += 1;
      pendingAction.current = null;
      stop();
    };
  }, []);

  // Check the partner code as it is typed (after a short pause), and show whose code it is.
  useEffect(() => {
    if (partnerInput.trim() === '') {
      setPartnerCheck({ state: 'empty' });
      return;
    }
    const code = normalizePartnerCode(partnerInput);
    if (code === null) {
      // Half typed is not wrong yet: say so only once the typing pauses (or the field is left), never per keystroke.
      setPartnerCheck({ state: 'typing' });
      const settled = setTimeout(() => setPartnerCheck({ state: 'malformed' }), 350);
      return () => clearTimeout(settled);
    }
    const lookup = window.capturePointDesktop?.lookupPartnerCode;
    if (!lookup) {
      setPartnerCheck({ state: 'unavailable', code });
      return;
    }
    const known = partnerAnswers.current.get(code);
    if (known) {
      setPartnerCheck(known);
      return;
    }
    let current = true;
    setPartnerCheck({ state: 'checking', code });
    const timer = setTimeout(() => {
      // Never longer than a few seconds: past that it reads "couldn't check", which never blocks the purchase.
      void partnerAnswers.current
        .check(code, () =>
          partnerLookupWithDeadline(lookup, code).then((result): PartnerCheck =>
            result.accepted
              ? {
                  state: 'accepted',
                  code: result.code,
                  ...(result.customerDiscount ? { discount: result.customerDiscount } : {}),
                }
              : result.reason === 'not-recognized'
                ? { state: 'not-recognized', code }
                : { state: 'unavailable', code },
          ),
        )
        .then((check) => {
          if (current) setPartnerCheck(check);
        })
        .catch(() => {
          if (current) setPartnerCheck({ state: 'unavailable', code });
        });
    }, 350);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [partnerInput]);
  // The partner code's status line says the code is wrong or could not be checked: which message, never the code.
  const partnerProblem =
    partnerCheck.state === 'malformed' ||
    partnerCheck.state === 'not-recognized' ||
    partnerCheck.state === 'unavailable';
  useEffect(() => {
    if (partnerProblem) reportEvent('ui.error:partner-code-failed', { attrs: { kind: 'status' } });
  }, [partnerProblem, partnerCheck]);

  // An invoice request already waiting with Ademero (kept by main across restarts, settled once licensed,
  // and marked denied once Ademero says staff turned it down).
  useEffect(() => {
    let mounted = true;
    void window.capturePointDesktop
      ?.getPendingInvoiceRequest?.()
      .then((request) => {
        if (mounted)
          setPendingInvoice(
            request
              ? {
                  plan: request.plan,
                  submittedUtc: request.submittedUtc,
                  ...(request.state ? { state: request.state } : {}),
                }
              : null,
          );
      })
      .catch(() => undefined);
    return () => {
      mounted = false;
    };
  }, [state?.runtimeState]);

  // The open-checkout line lasts while main waits for the payment, and ends once this computer is licensed.
  useEffect(() => {
    if (!openCheckout) return;
    if (state?.runtimeState === 'active') {
      writeOpenCheckout(null);
      setOpenCheckout(null);
      return;
    }
    const timer = setInterval(() => setClockMs(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, [openCheckout, state?.runtimeState]);
  const openCheckoutLine = openCheckoutMessage(openCheckout, clockMs);

  useEffect(() => {
    let mounted = true;
    void window.capturePointDesktop
      ?.getV6LicenseContact?.()
      .then((contact) => {
        if (mounted && contact && !draftEdited.current)
          setDraft({ ...contact, phone: contact.phone ?? '' });
      })
      .catch(() => {
        if (mounted)
          setProblem(
            'Saved contact details could not be read. Please enter them again.',
            'license-load-failed',
          );
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Decision 7: the free extension is offered near the end (and after it);
  // after it is used, staff approve more time, or the customer buys.
  // After the free extension, more time is a staff-approved request (one open at a time).
  const { freeExtension: canExtendNow, askForMoreTime: canAskForTime } = trialOffers(state);
  const moreTimeMessage = moreTimeRequestMessage(state);
  const canBuy = Boolean(
    state &&
    ['trial-active', 'trial-expired', 'license-expired', 'deactivated'].includes(
      state.runtimeState,
    ),
  );
  // "Choose your plan": the plan this computer is on (its page snapshot) on a license, nothing on a trial.
  const allowance = usePageAllowance();
  const currentPlan = useMemo((): { plan: PlanKey; interval: CheckoutPlan | null } | null => {
    if (state?.runtimeState !== 'active' || !allowance?.tier) return null;
    return allowance.tier === 'custom'
      ? { plan: 'custom', interval: null }
      : { plan: 'lite', interval: allowance.tier === 'lite-yearly' ? 'yearly' : 'monthly' };
  }, [state?.runtimeState, allowance?.tier]);
  // A licensed Lite computer changes plan here: the other interval or Custom go to Ademero as a request.
  const canChangePlan = currentPlan?.plan === 'lite';
  const [planKey, setPlanKey] = useState<PlanKey>('lite');
  /** Which plan-page button the last outcome answers: it is shown beside that button. */
  const [outcomeAt, setOutcomeAt] = useState<'buy' | 'bill'>('buy');
  const trialPill =
    state?.runtimeState === 'trial-active' &&
    state.secondsRemaining !== null &&
    state.secondsRemaining > 0
      ? `${remainingLabel(state.secondsRemaining)} in your trial`
      : state?.runtimeState === 'trial-expired'
        ? 'Your trial has ended'
        : null;
  const discount = partnerCheck.state === 'accepted' ? (partnerCheck.discount ?? null) : null;
  const planShown =
    (action === 'checkout' && canBuy && !paidByCard) || (action === 'change-plan' && canChangePlan);
  const scrollToPlans = () =>
    requestAnimationFrame(() =>
      document
        .getElementById('plan-page-title')
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
    );
  const openChangePlan = () => {
    setPlanKey(currentPlan?.plan ?? 'lite');
    if (currentPlan?.interval) setPlan(currentPlan.interval);
    setProblem(null);
    setNotice(null);
    setAction('change-plan');
    scrollToPlans();
  };
  /** Custom, or a change a licensed computer asks for: the existing inquiry, with the note filled in. */
  const askAdemero = (text: string) => {
    setNote(text);
    setNoteOpen(true);
    setProblem(null);
    setNotice(null);
    setAction('questions');
  };

  useEffect(() => {
    const onIntent = (event: Event) => {
      const intent = (event as CustomEvent<string>).detail;
      setAction(
        intent === 'buy'
          ? 'checkout'
          : intent === 'extend'
            ? 'extend'
            : intent === 'more-time'
              ? 'more-time'
              : null,
      );
      openedFrom.current = takeLicenseFrom() ?? openedFrom.current;
    };
    window.addEventListener('capturepoint-license-intent', onIntent);
    return () => window.removeEventListener('capturepoint-license-intent', onIntent);
  }, []);

  useEffect(() => {
    if (!state || opened.current) return;
    opened.current = true;
    const intent = requestedIntent.current;
    if (intent) {
      setAction(intent === 'buy' ? 'checkout' : intent);
      return;
    }
    if (!state.canStartNewWork) setAction(state.canAddTrialDays ? 'extend' : 'checkout');
    // On a trial, Buy now is what this screen is for: its form is open from the start.
    else if (
      ['trial-active', 'trial-expired', 'license-expired', 'deactivated'].includes(
        state.runtimeState,
      )
    )
      setAction('checkout');
  }, [state]);

  // With an invoice request waiting, the screen says so instead of pushing Buy now again (a second, card payment).
  const invoiceNoticeShown = useRef(false);
  useEffect(() => {
    if (!invoiceWaiting || invoiceNoticeShown.current) return;
    invoiceNoticeShown.current = true;
    setAction((current) => (current === 'checkout' ? null : current));
  }, [invoiceWaiting]);

  // A card payment already bought this computer (it withdrew the invoice request): while the paid license is on
  // its way the buy form stays closed, so neither Buy now (a second payment) nor Bill us instead is offered.
  useEffect(() => {
    if (paidByCard) setAction((current) => (current === 'checkout' ? null : current));
  }, [paidByCard]);

  const presentation = useMemo(
    () =>
      !state && problem
        ? {
            title: 'License unavailable',
            detail: 'The license could not be read',
            tone: 'attention',
            icon: TriangleAlert,
          }
        : statusPresentation(state),
    [state, problem],
  );
  const StatusIcon = presentation.icon;
  const activationPending = state?.activationRequestState === 'pending';
  const inquiryReceived = state?.salesInquiryState === 'received';

  const saveOptIn = async (input: { choice: 'decline' }) => {
    const set = window.capturePointDesktop?.setEngagementConsent;
    if (!set) return;
    try {
      await set(input);
      setNotice('Setup tips turned off');
    } catch (error) {
      setProblem(
        error instanceof Error ? error.message : 'Your choice could not be saved.',
        'tips-consent-failed',
      );
    }
  };

  const refresh = async () => {
    const api = window.capturePointDesktop;
    if (!api || pendingAction.current) return;
    const owner = {};
    pendingAction.current = owner;
    firstReadRevision.current += 1;
    setBusy('refresh');
    setProblem(null);
    setNotice(null);
    try {
      const next = await api.refreshV6License();
      if (live.current && pendingAction.current === owner) {
        setState(next);
        setNotice(
          next.reachabilityState === 'confirmed-online'
            ? 'License checked'
            : 'Using the last confirmed license',
        );
      }
    } catch {
      if (live.current && pendingAction.current === owner)
        setProblem(
          state
            ? 'The license could not be checked. Showing the last received license; current access has not been rechecked.'
            : 'The license could not be checked. Current access is unknown; try again.',
          'license-check-failed',
        );
    } finally {
      if (pendingAction.current === owner) {
        pendingAction.current = null;
        if (live.current) setBusy(null);
      }
    }
  };

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  // A click with details missing marks each field and names them, so it never looks like it did nothing.
  const refuseMissing = () => {
    const problems = licenseContactProblems(draft);
    setShowMissing(true);
    if (problems.phone) setNoteOpen(true);
    setNotice(null);
    setProblem(licenseContactProblemSummary(problems), 'contact-details-needed');
    announceOutcome('message');
  };

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const submit = async () => {
    if (!contactReady(draft)) {
      refuseMissing();
      return;
    }
    const api = window.capturePointDesktop;
    // The plan page's change mode sends nothing itself: its buttons open the inquiry.
    if (
      !api ||
      !action ||
      action === 'change-plan' ||
      pendingAction.current ||
      !contactReady(draft)
    )
      return;
    const owner = {};
    pendingAction.current = owner;
    firstReadRevision.current += 1;
    setBusy(action);
    setProblem(null);
    setNotice(null);
    try {
      if (action === 'checkout') {
        if (!api.startCheckout) throw new Error('Card checkout is not available in this build.');
        const contact = toContact(draft);
        const partner = partnerCodeForPurchase(partnerCheck);
        if (partner.blocked) return;
        const started = await api.startCheckout({
          plan,
          ...contact,
          ...(partner.code ? { partnerCode: partner.code } : {}),
          from: openedFrom.current,
        });
        if (!live.current || pendingAction.current !== owner) return;
        if (started.started) {
          setAction(null);
          setCheckoutClosed(false);
          const opened = { plan: started.plan, openedUtc: new Date().toISOString() };
          writeOpenCheckout(opened);
          setOpenCheckout(opened);
          setClockMs(Date.now());
          announceOutcome('checkout');
        } else if (started.reason === 'unavailable') {
          // Card payment is closed: the form, the partner code and "Bill us instead" all stay.
          setCheckoutClosed(true);
          announceOutcome('closed');
        } else {
          // A hiccup, a refusal or an existing subscription: stay on the form so the buyer can try again or bill instead.
          setProblem(started.message, 'checkout-failed');
          announceOutcome('message');
        }
        return;
      }
      // The tips line was on the form the person sent: the extra week comes with tips.
      const tipsWithWeek = action === 'extend' && extraWeekTips;
      const next =
        action === 'extend'
          ? await api.extendV6Trial(toContact(draft))
          : action === 'more-time'
            ? await api.requestV6MoreTime({
                ...toContact(draft),
                ...(note.trim() ? { message: note.trim() } : {}),
              })
            : await api.requestV6Activation({
                ...toContact(draft),
                requestIntent: action,
                message: [
                  note.trim(),
                  action === 'buy' && 'code' in partnerCheck
                    ? `Partner code: ${partnerCheck.code}`
                    : '',
                ]
                  .filter(Boolean)
                  .join('\n'),
              });
      if (!live.current || pendingAction.current !== owner) return;
      setState(next);
      setAction(null);
      if (tipsWithWeek && api.setEngagementConsent) {
        const contact = toContact(draft);
        void api
          .setEngagementConsent({
            choice: 'opt-in',
            ...(contact.contactName ? { name: contact.contactName } : {}),
            email: contact.email,
            source: 'extra-week',
          })
          .catch(() => {
            if (live.current)
              setProblem(
                'Your extra week was added, but setup tips could not be turned on.',
                'tips-consent-failed',
              );
          });
      }
      setNotice(
        action === 'extend'
          ? '7 days added to this trial'
          : action === 'more-time'
            ? 'Request sent. Ademero reviews it and will follow up.'
            : action === 'activate'
              ? 'Activation request received. No payment was taken. Access will update after approval.'
              : 'Your request has been sent to Ademero. We will follow up using your contact details. No purchase has been made.',
      );
      announceOutcome('message');
    } catch (error) {
      // Details main refused say which to fix (they are written for the buyer); anything else says try again.
      if (live.current && pendingAction.current === owner) {
        setProblem(
          error instanceof Error && error.message.includes('Development trial simulation:')
            ? error.message.slice(error.message.indexOf('Development trial simulation:'))
            : buyerFacingLicenseError(
                error,
                action === 'extend'
                  ? 'The trial could not be extended. Your entries stay in this form so you can try again.'
                  : 'Your request could not be sent. Your entries stay in this form so you can try again.',
              ),
          action === 'checkout'
            ? 'checkout-failed'
            : action === 'extend'
              ? 'extend-failed'
              : action === 'more-time'
                ? 'more-time-failed'
                : 'activation-failed',
        );
        announceOutcome('message');
      }
    } finally {
      if (pendingAction.current === owner) {
        pendingAction.current = null;
        if (live.current) setBusy(null);
      }
    }
  };

  // "Bill us instead": the same details go to Ademero as an invoice request; approval activates this computer.
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const requestInvoice = async () => {
    if (!contactReady(draft)) {
      refuseMissing();
      return;
    }
    const api = window.capturePointDesktop;
    const partner = partnerCodeForPurchase(partnerCheck);
    if (!api?.requestInvoice || pendingAction.current || !contactReady(draft) || partner.blocked)
      return;
    const owner = {};
    pendingAction.current = owner;
    setBusy('checkout');
    setInvoiceBusy(true);
    setProblem(null);
    setNotice(null);
    try {
      const result = await api.requestInvoice({
        plan,
        ...toContact(draft),
        ...(partner.code ? { partnerCode: partner.code } : {}),
      });
      if (!live.current || pendingAction.current !== owner) return;
      if (result.requested) {
        setAction(null);
        // Ademero closed the card checkout this request replaces: the screen no longer offers or waits for it.
        writeOpenCheckout(null);
        setOpenCheckout(null);
        if (result.submittedUtc)
          setPendingInvoice({ plan: result.plan, submittedUtc: result.submittedUtc });
        invoiceNoticeShown.current = true;
        setNotice(
          result.existing
            ? `An invoice request is already with Ademero (${planPrice(result.plan)}). Contact Ademero to change it.`
            : `Invoice requested (${planPrice(result.plan)}). Ademero emails the invoice to ${draft.email.trim()}, and this computer activates when it's approved.`,
        );
      } else setProblem(result.message, 'invoice-failed');
      announceOutcome('message');
    } catch (error) {
      if (live.current && pendingAction.current === owner) {
        setProblem(
          buyerFacingLicenseError(
            error,
            'Your invoice request could not be sent. Your entries stay in this form so you can try again.',
          ),
          'invoice-failed',
        );
        announceOutcome('message');
      }
    } finally {
      if (pendingAction.current === owner) {
        pendingAction.current = null;
        if (live.current) {
          setBusy(null);
          setInvoiceBusy(false);
        }
      }
    }
  };
  const partnerBlocked = action === 'checkout' && partnerCodeForPurchase(partnerCheck).blocked;
  const problems = showMissing ? licenseContactProblems(draft) : {};
  // An Enterprise quote asked from the pages card opens the same inquiry form on a licensed computer.
  const deskShown = Boolean(
    canBuy ||
    state?.canAddTrialDays ||
    state?.canRequestActivation ||
    activationPending ||
    inquiryReceived ||
    moreTimeMessage ||
    action === 'questions' ||
    (action === 'change-plan' && canChangePlan),
  );
  const formOpen =
    action !== null &&
    Boolean(
      action === 'extend'
        ? canExtendNow
        : action === 'activate'
          ? state?.canRequestActivation
          : action === 'more-time'
            ? canAskForTime
            : action === 'checkout'
              ? canBuy && !paidByCard
              : action === 'change-plan'
                ? canChangePlan
                : !inquiryReceived,
    );
  // Buy now on the plan page names the price paid: less the partner's discount when one applies.
  const buyPrice = `${formatPrice(litePrice(plan, discount).due)} a ${plan === 'yearly' ? 'year' : 'month'}`;
  const choosePlanInterval = (next: CheckoutPlan) => {
    setPlan(next);
    if (next === 'monthly') reportEvent('buy.plan:monthly');
    else reportEvent('buy.plan:yearly');
  };
  // Where the last outcome is shown: beside the form's buttons while the form is open, else at the top of
  // the desk where the button that closed the form was, else under the status card.
  const message =
    notice || problem ? (
      <div
        key={problem ?? notice ?? ''}
        ref={messageRef}
        tabIndex={-1}
        className={`license-message ${problem ? 'problem' : 'success'}`}
        role={problem ? 'alert' : 'status'}
        data-telemetry={problem && problemCode ? `error:${problemCode}` : undefined}
      >
        {problem ? <TriangleAlert size={16} /> : <Check size={16} />}
        <span>{problem ?? notice}</span>
      </div>
    ) : null;

  return (
    <div className="computer-section license-settings" aria-labelledby="settings-license-title">
      <header className="computer-local-heading">
        <div>
          <span className="eyebrow">Access</span>
          <h2 id="settings-license-title">License</h2>
        </div>
      </header>

      <section className={`license-status-card ${presentation.tone}`} aria-live="polite">
        <span className="license-status-mark">
          <StatusIcon size={25} />
        </span>
        <div className="license-status-copy">
          <span>
            {state?.expiresUtc
              ? `${state.runtimeState === 'trial-expired' ? 'Ended' : 'Through'} ${dateLabel(state.expiresUtc)}`
              : presentation.detail}
          </span>
          <strong>
            {presentation.title}
            {state &&
            (state.runtimeState === 'trial-active' || state.runtimeState === 'active') &&
            state.secondsRemaining !== null
              ? ` · ${remainingLabel(state.secondsRemaining)}`
              : ''}
          </strong>
        </div>
        {canChangePlan && action !== 'change-plan' && (
          <button
            type="button"
            className="secondary-button"
            data-telemetry="open-change-plan"
            onClick={openChangePlan}
            disabled={busy !== null}
          >
            Change plan
          </button>
        )}
        <button
          className="icon-button"
          onClick={() => void refresh()}
          disabled={busy !== null}
          aria-label="Check again"
          title="Check again"
        >
          <RefreshCw size={18} className={busy === 'refresh' ? 'spin' : undefined} />
        </button>
      </section>

      {state?.supportRecovery && (
        <SupportRecoveryNotice installationId={state.supportRecovery.installationId} />
      )}

      {!deskShown && message}

      {deskShown && (
        <section
          className={`license-actions-card license-desk${formOpen && planShown ? ' choosing' : ''}`}
        >
          <div className="license-desk-main">
            {!formOpen && message}
            {moreTimeMessage && (
              <p role="status" className="license-more-time">
                {moreTimeMessage}
              </p>
            )}
            {inquiryReceived && (
              <p role="status">
                Sales inquiry received. Ademero will contact you. This computer sends one inquiry;
                for anything else, contact Ademero directly.
              </p>
            )}
            {canBuy && pendingInvoice && (
              <p role="status" className="license-invoice-pending">
                <FileText size={15} aria-hidden="true" /> {pendingInvoiceMessage(pendingInvoice)}
              </p>
            )}
            {canBuy && openCheckoutLine && (
              <p
                ref={checkoutLineRef}
                tabIndex={-1}
                role="status"
                className="license-invoice-pending"
              >
                <ShoppingCart size={15} aria-hidden="true" /> {openCheckoutLine}
              </p>
            )}
            {action === null && canBuy && !invoiceWaiting && !paidByCard && !openCheckoutLine && (
              <p className="license-price">
                <strong>{capturePointStationPricing.label}</strong>
              </p>
            )}

            {formOpen &&
              planShown &&
              (action === 'checkout' ? (
                <form
                  className="license-contact-form license-plan-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (planKey === 'lite') void submit();
                  }}
                >
                  <PlanPage
                    interval={plan}
                    onInterval={choosePlanInterval}
                    selected={planKey}
                    onSelect={setPlanKey}
                    current={null}
                    trial={trialPill}
                    discount={discount}
                    action={
                      planKey === 'custom' ? (
                        <button
                          type="button"
                          className="plan-spotlight__cta"
                          data-telemetry="plan-custom-quote"
                          disabled={busy !== null}
                          onClick={() => askAdemero("I'd like an Enterprise quote.")}
                        >
                          <Handshake size={17} aria-hidden="true" />
                          Ask for an Enterprise quote
                        </button>
                      ) : (
                        <>
                          <button
                            type="submit"
                            className="plan-spotlight__cta"
                            data-telemetry="buy-now"
                            disabled={busy !== null || partnerBlocked || checkoutClosed}
                            onClick={() => setOutcomeAt('buy')}
                          >
                            {busy === 'checkout' && !invoiceBusy ? (
                              <LoaderCircle size={17} className="spin" />
                            ) : (
                              <ShoppingCart size={17} aria-hidden="true" />
                            )}
                            {`${openCheckoutLine && openCheckout?.plan === plan ? 'Open the checkout again' : 'Buy now'} · ${buyPrice}`}
                          </button>
                          {/* What Buy now led to is shown under it, where the buyer clicked. */}
                          {checkoutClosed && (
                            <p
                              ref={closedRef}
                              tabIndex={-1}
                              className="license-checkout-closed"
                              role="status"
                            >
                              <Clock3 size={15} aria-hidden="true" /> Card payment isn't open right
                              now. Bill us instead, or{' '}
                              <button
                                type="button"
                                className="quiet-link"
                                onClick={() => setAction('buy')}
                              >
                                send your details
                              </button>
                              .
                            </p>
                          )}
                          {outcomeAt === 'buy' && message}
                        </>
                      )
                    }
                  >
                    {planKey === 'lite' && (
                      <div className="plan-details" aria-label="Your details" role="group">
                        <div className="license-form-grid">
                          <label>
                            <span>
                              Your name
                              {problems.contactName && (
                                <small className="license-missing">{problems.contactName}</small>
                              )}
                            </span>
                            <input
                              aria-invalid={Boolean(problems.contactName)}
                              maxLength={licenseContactLimits.contactName}
                              value={draft.contactName}
                              onChange={(event) => {
                                draftEdited.current = true;
                                setDraft((current) => ({
                                  ...current,
                                  contactName: event.target.value,
                                }));
                              }}
                              autoComplete="name"
                            />
                          </label>
                          <label>
                            <span>
                              Email
                              {problems.email && (
                                <small className="license-missing">{problems.email}</small>
                              )}
                            </span>
                            <input
                              aria-invalid={Boolean(problems.email)}
                              maxLength={licenseContactLimits.email}
                              type="email"
                              value={draft.email}
                              onChange={(event) => {
                                draftEdited.current = true;
                                setDraft((current) => ({ ...current, email: event.target.value }));
                              }}
                              autoComplete="email"
                            />
                          </label>
                          <label>
                            <span>
                              Company
                              {problems.companyName && (
                                <small className="license-missing">{problems.companyName}</small>
                              )}
                            </span>
                            <input
                              aria-invalid={Boolean(problems.companyName)}
                              maxLength={licenseContactLimits.companyName}
                              value={draft.companyName}
                              onChange={(event) => {
                                draftEdited.current = true;
                                setDraft((current) => ({
                                  ...current,
                                  companyName: event.target.value,
                                }));
                              }}
                              autoComplete="organization"
                            />
                          </label>
                          {action === 'checkout' && (
                            <label data-telemetry="partner-code" className="license-partner">
                              <span id="license-partner-label">
                                Partner code <small>Optional</small>
                              </span>
                              {/* Named by its label text only: the check's status is its description, never part of its name. */}
                              <input
                                value={partnerInput}
                                maxLength={8}
                                spellCheck={false}
                                autoComplete="off"
                                aria-labelledby="license-partner-label"
                                aria-describedby="license-partner-status"
                                aria-invalid={
                                  partnerCheck.state === 'not-recognized' ||
                                  partnerCheck.state === 'malformed'
                                }
                                onChange={(event) =>
                                  setPartnerInput(event.target.value.toUpperCase())
                                }
                                onBlur={() =>
                                  setPartnerCheck((check) =>
                                    check.state === 'typing' ? { state: 'malformed' } : check,
                                  )
                                }
                              />
                              <small
                                id="license-partner-status"
                                className={`license-partner-status ${partnerCheck.state}`}
                                role="status"
                              >
                                {partnerCheck.state === 'accepted' ? (
                                  <>
                                    <Check size={13} aria-hidden="true" /> Partner code accepted
                                  </>
                                ) : partnerCheck.state === 'checking' ? (
                                  'Checking…'
                                ) : partnerCheck.state === 'not-recognized' ? (
                                  "Not recognized. Check the code; if it's right, go ahead and Ademero checks it with your order."
                                ) : partnerCheck.state === 'malformed' ? (
                                  'Partner codes are 4 letters or numbers. Check it, or clear it to buy without one.'
                                ) : partnerCheck.state === 'unavailable' ? (
                                  "Couldn't check the code now; Ademero checks it with your order."
                                ) : (
                                  'From the Ademero partner who referred you.'
                                )}
                              </small>
                            </label>
                          )}
                        </div>
                        {noteOpen ? (
                          <div className="license-note-group">
                            <div className="license-form-grid">
                              <label>
                                <span>
                                  Phone{' '}
                                  {problems.phone ? (
                                    <small className="license-missing">{problems.phone}</small>
                                  ) : (
                                    <small>Optional</small>
                                  )}
                                </span>
                                <input
                                  type="tel"
                                  aria-invalid={Boolean(problems.phone)}
                                  maxLength={licenseContactLimits.phone}
                                  value={draft.phone}
                                  onChange={(event) => {
                                    draftEdited.current = true;
                                    setDraft((current) => ({
                                      ...current,
                                      phone: event.target.value,
                                    }));
                                  }}
                                  autoComplete="tel"
                                />
                              </label>
                            </div>
                            {action !== 'checkout' &&
                              action !== 'extend' &&
                              action !== 'activate' && (
                                <label className="license-note">
                                  <span>
                                    What would you like to discuss? <small>Optional</small>
                                  </span>
                                  <textarea
                                    rows={2}
                                    maxLength={1500}
                                    value={note}
                                    onChange={(event) => setNote(event.target.value)}
                                  />
                                </label>
                              )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="quiet-link"
                            onClick={() => setNoteOpen(true)}
                          >
                            {action === 'checkout' || action === 'extend' || action === 'activate'
                              ? 'Add a phone number'
                              : 'Add a phone number or a note'}
                          </button>
                        )}
                        {action === 'checkout' && invoiceWaiting && (
                          <p className="license-checkout-closed" role="status">
                            <FileText size={15} aria-hidden="true" /> You already asked for an
                            invoice. Paying by card instead withdraws that request.
                          </p>
                        )}
                        <div className="plan-details-foot">
                          {action === 'checkout' && window.capturePointDesktop?.requestInvoice && (
                            <button
                              type="button"
                              className="secondary-button"
                              data-telemetry="bill-us-instead"
                              disabled={busy !== null || partnerBlocked}
                              onClick={() => {
                                setOutcomeAt('bill');
                                void requestInvoice();
                              }}
                            >
                              {invoiceBusy ? (
                                <LoaderCircle size={17} className="spin" />
                              ) : (
                                <FileText size={17} />
                              )}
                              Bill us instead (invoice)
                            </button>
                          )}
                          <p className="license-pay-note">
                            Card payment is by Stripe in your browser; activation is automatic. With
                            an invoice, Ademero approves the order and emails it.
                          </p>
                        </div>
                        {outcomeAt === 'bill' && message}
                      </div>
                    )}
                  </PlanPage>
                </form>
              ) : (
                <div className="license-plan-form">
                  <PlanPage
                    interval={plan}
                    onInterval={choosePlanInterval}
                    selected={planKey}
                    onSelect={setPlanKey}
                    current={currentPlan}
                    trial={null}
                    discount={null}
                    action={
                      <>
                        {planKey === 'custom' ? (
                          <button
                            type="button"
                            className="plan-spotlight__cta"
                            data-telemetry="plan-custom-quote"
                            onClick={() =>
                              askAdemero("I'd like an Enterprise quote for this computer.")
                            }
                          >
                            <Handshake size={17} aria-hidden="true" />
                            Ask for an Enterprise quote
                          </button>
                        ) : plan === currentPlan?.interval ? (
                          <button
                            type="button"
                            className="plan-spotlight__cta plan-spotlight__cta--current"
                            disabled
                          >
                            Your plan
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="plan-spotlight__cta"
                            data-telemetry="plan-switch"
                            onClick={() =>
                              askAdemero(
                                `I'd like to switch this computer to Lite, paid ${plan === 'yearly' ? 'yearly' : 'monthly'}.`,
                              )
                            }
                          >
                            {plan === 'yearly'
                              ? 'Ask to switch to annual'
                              : 'Ask to switch to monthly'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="plan-spotlight__secondary"
                          onClick={() => setAction(null)}
                        >
                          Keep my plan
                        </button>
                      </>
                    }
                  />
                  {message}
                </div>
              ))}

            {formOpen && !planShown && (
              <form
                className="license-contact-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void submit();
                }}
              >
                <div className="license-form-title">
                  <strong>
                    {action === 'checkout'
                      ? 'Buy Paige for this station'
                      : action === 'extend'
                        ? 'Another week to explore'
                        : action === 'more-time'
                          ? 'Ask Ademero for more time'
                          : action === 'activate'
                            ? 'Already have a license?'
                            : action === 'buy' && checkoutClosed
                              ? 'Buy Paige for this station'
                              : 'Ask Ademero'}
                  </strong>
                  <span>
                    {action === 'checkout'
                      ? 'Pay in your browser; activation is automatic.'
                      : action === 'extend'
                        ? 'One free extension. No purchase.'
                        : action === 'more-time'
                          ? 'Ademero reviews each request.'
                          : action === 'activate'
                            ? 'No payment. Access starts after approval.'
                            : action === 'buy' && checkoutClosed
                              ? `${capturePointStationPricing.label}.`
                              : "We'll follow up by email."}
                  </span>
                </div>
                <div className="license-form-grid">
                  <label>
                    <span>
                      Your name
                      {problems.contactName && (
                        <small className="license-missing">{problems.contactName}</small>
                      )}
                    </span>
                    <input
                      aria-invalid={Boolean(problems.contactName)}
                      maxLength={licenseContactLimits.contactName}
                      value={draft.contactName}
                      onChange={(event) => {
                        draftEdited.current = true;
                        setDraft((current) => ({ ...current, contactName: event.target.value }));
                      }}
                      autoComplete="name"
                    />
                  </label>
                  <label>
                    <span>
                      Email
                      {problems.email && (
                        <small className="license-missing">{problems.email}</small>
                      )}
                    </span>
                    <input
                      aria-invalid={Boolean(problems.email)}
                      maxLength={licenseContactLimits.email}
                      type="email"
                      value={draft.email}
                      onChange={(event) => {
                        draftEdited.current = true;
                        setDraft((current) => ({ ...current, email: event.target.value }));
                      }}
                      autoComplete="email"
                    />
                  </label>
                  <label>
                    <span>
                      Company
                      {problems.companyName && (
                        <small className="license-missing">{problems.companyName}</small>
                      )}
                    </span>
                    <input
                      aria-invalid={Boolean(problems.companyName)}
                      maxLength={licenseContactLimits.companyName}
                      value={draft.companyName}
                      onChange={(event) => {
                        draftEdited.current = true;
                        setDraft((current) => ({ ...current, companyName: event.target.value }));
                      }}
                      autoComplete="organization"
                    />
                  </label>
                  {action === 'checkout' && (
                    <label data-telemetry="partner-code" className="license-partner">
                      <span id="license-partner-label">
                        Partner code <small>Optional</small>
                      </span>
                      {/* Named by its label text only: the check's status is its description, never part of its name. */}
                      <input
                        value={partnerInput}
                        maxLength={8}
                        spellCheck={false}
                        autoComplete="off"
                        aria-labelledby="license-partner-label"
                        aria-describedby="license-partner-status"
                        aria-invalid={
                          partnerCheck.state === 'not-recognized' ||
                          partnerCheck.state === 'malformed'
                        }
                        onChange={(event) => setPartnerInput(event.target.value.toUpperCase())}
                        onBlur={() =>
                          setPartnerCheck((check) =>
                            check.state === 'typing' ? { state: 'malformed' } : check,
                          )
                        }
                      />
                      <small
                        id="license-partner-status"
                        className={`license-partner-status ${partnerCheck.state}`}
                        role="status"
                      >
                        {partnerCheck.state === 'accepted' ? (
                          <>
                            <Check size={13} aria-hidden="true" /> Partner code accepted
                          </>
                        ) : partnerCheck.state === 'checking' ? (
                          'Checking…'
                        ) : partnerCheck.state === 'not-recognized' ? (
                          "Not recognized. Check the code; if it's right, go ahead and Ademero checks it with your order."
                        ) : partnerCheck.state === 'malformed' ? (
                          'Partner codes are 4 letters or numbers. Check it, or clear it to buy without one.'
                        ) : partnerCheck.state === 'unavailable' ? (
                          "Couldn't check the code now; Ademero checks it with your order."
                        ) : (
                          'From the Ademero partner who referred you.'
                        )}
                      </small>
                    </label>
                  )}
                </div>
                {noteOpen ? (
                  <div className="license-note-group">
                    <div className="license-form-grid">
                      <label>
                        <span>
                          Phone{' '}
                          {problems.phone ? (
                            <small className="license-missing">{problems.phone}</small>
                          ) : (
                            <small>Optional</small>
                          )}
                        </span>
                        <input
                          type="tel"
                          aria-invalid={Boolean(problems.phone)}
                          maxLength={licenseContactLimits.phone}
                          value={draft.phone}
                          onChange={(event) => {
                            draftEdited.current = true;
                            setDraft((current) => ({ ...current, phone: event.target.value }));
                          }}
                          autoComplete="tel"
                        />
                      </label>
                    </div>
                    {action !== 'checkout' && action !== 'extend' && action !== 'activate' && (
                      <label className="license-note">
                        <span>
                          What would you like to discuss? <small>Optional</small>
                        </span>
                        <textarea
                          rows={2}
                          maxLength={1500}
                          value={note}
                          onChange={(event) => setNote(event.target.value)}
                        />
                      </label>
                    )}
                  </div>
                ) : (
                  <button type="button" className="quiet-link" onClick={() => setNoteOpen(true)}>
                    {action === 'checkout' || action === 'extend' || action === 'activate'
                      ? 'Add a phone number'
                      : 'Add a phone number or a note'}
                  </button>
                )}
                {action === 'extend' && extraWeekTips && (
                  <p className="license-extra-week-tips">
                    <Mail size={15} aria-hidden="true" />
                    {EXTRA_WEEK_TIPS_LINE}
                  </p>
                )}
                <div className="license-form-actions">
                  <button
                    type="submit"
                    className="primary-button"
                    data-telemetry={
                      action === 'checkout'
                        ? 'buy-now'
                        : action === 'extend'
                          ? 'add-free-days'
                          : action === 'activate'
                            ? 'send-activation-request'
                            : action === 'more-time'
                              ? 'ask-for-more-time'
                              : 'send-to-ademero'
                    }
                    disabled={
                      busy !== null || partnerBlocked || (action === 'checkout' && checkoutClosed)
                    }
                  >
                    {busy === action && !invoiceBusy ? (
                      <LoaderCircle size={17} className="spin" />
                    ) : (
                      <Check size={17} />
                    )}
                    {action === 'checkout'
                      ? `${openCheckoutLine && openCheckout?.plan === plan ? 'Open the checkout again' : 'Buy now'} · ${planPrice(plan)}`
                      : action === 'extend'
                        ? 'Add 7 free days'
                        : action === 'activate'
                          ? 'Send activation request'
                          : action === 'more-time'
                            ? 'Ask for more time'
                            : 'Send to Ademero'}
                  </button>
                </div>
                {message}
              </form>
            )}
          </div>
          <aside className="license-other" aria-label="Other options">
            {activationPending && (
              <p className="license-pending" role="status">
                <LoaderCircle size={14} className="spin" /> Activation pending
              </p>
            )}
            {canBuy && !paidByCard && action !== 'checkout' && (
              <button
                className={invoiceWaiting ? 'quiet-link' : 'secondary-button'}
                data-telemetry="open-buy-now"
                onClick={() => setAction('checkout')}
                disabled={busy !== null}
              >
                {invoiceWaiting ? (
                  'Pay by card instead'
                ) : openCheckoutLine ? (
                  <>
                    <ShoppingCart size={17} /> Checkout already open
                  </>
                ) : (
                  <>
                    <ShoppingCart size={17} /> Buy now
                  </>
                )}
              </button>
            )}
            {canExtendNow && action !== 'extend' && (
              <button
                className="quiet-link"
                data-telemetry="open-add-free-days"
                onClick={() => setAction('extend')}
                disabled={busy !== null}
              >
                Add 7 free days
              </button>
            )}
            {canAskForTime && action !== 'more-time' && (
              <button
                className="quiet-link"
                data-telemetry="open-ask-for-more-time"
                onClick={() => setAction('more-time')}
                disabled={busy !== null}
              >
                Ask for more time
              </button>
            )}
            {state?.canRequestActivation && action !== 'activate' && (
              <button
                className="quiet-link"
                data-telemetry="open-activate-license"
                onClick={() => setAction('activate')}
                disabled={busy !== null}
              >
                Activate my license
              </button>
            )}
            {!inquiryReceived && action !== 'questions' && action !== 'buy' && (
              <button
                className="quiet-link"
                data-telemetry="open-pricing-questions"
                onClick={() => setAction('questions')}
                disabled={busy !== null}
              >
                Pricing &amp; questions
              </button>
            )}
          </aside>
        </section>
      )}

      <PageAllowancePanel
        onCustomQuote={() => {
          setNote("I'd like an Enterprise quote for more pages.");
          setNoteOpen(true);
          setAction('questions');
        }}
        onChangePlan={canChangePlan ? openChangePlan : undefined}
      />

      {engagement?.consent.state === 'opted-in' && (
        <p className="license-tips-row">
          <Mail size={16} aria-hidden="true" />
          <span>Setup tips go to {engagement.consent.email}</span>
          <button
            type="button"
            className="quiet-link"
            onClick={() => void saveOptIn({ choice: 'decline' })}
          >
            Turn off
          </button>
        </p>
      )}
    </div>
  );
}
